import { createAppSlice } from "../../app/createAppSlice";
import { useAppSelector } from "../../app/hooks";

export const state = {
  length: {
    unit: "m",
    factor: 1.0,
  },
  force: {
    unit: "kN",
    factor: 1.0e-3,
  },
  moment: {
    unit: "Nm",
    factor: 1.0,
  },
  lineload: {
    unit: "kN/m",
    factor: 1.0e-3,
  },
  angle: {
    unit: "°",
    factor: 1.0,
  },
  stress: {
    unit: "MPa",
    factor: 1.0e-6,
  },
  strain: {
    unit: "‰",
    factor: 1.0e3,
  },
  mass: {
    unit: "kg",
    factor: 1.0,
  },
  sectional: {
    unit: "mm",
    factor: 1.0e3,
  },
  temperature: {
    unit: "°C",
    factor: 1.0,
  },
  deformation: {
    unit: "mm",
    factor: 1.0,
  },
  energy: {
    unit: "J",
    factor: 1.0,
  },
  power: {
    unit: "W",
    factor: 1.0,
  },
  utilisation: {
    unit: "%",
    factor: 1.0,
  },
};

const safeFactor = (factor: unknown, fallback = 1.0): number => {
  return typeof factor === "number" && Number.isFinite(factor) && factor > 0 ? factor : fallback;
};

const safeUnit = (unit: unknown, fallback: string): string => {
  return typeof unit === "string" && unit.length > 0 ? unit : fallback;
};

export const calculateDensityFactor = (massFactor: unknown, lengthFactor: unknown, fallback = 1): number => {
  const safeMassFactor = normalizePositiveFactor(massFactor, fallback);
  const safeLengthFactor = normalizePositiveFactor(lengthFactor, fallback);
  return safeMassFactor / Math.pow(safeLengthFactor, 3);
};

export const createDensityUnit = (massUnit: unknown, lengthUnit: unknown): string => {
  const safeMassUnit = safeUnit(massUnit, "kg");
  const safeLengthUnit = safeUnit(lengthUnit, "m");
  return `${safeMassUnit}/${safeLengthUnit}³`;
};

export const calculateLineLoadFactor = (forceFactor: unknown, lengthFactor: unknown, fallback = 1): number => {
  const safeForceFactor = normalizePositiveFactor(forceFactor, fallback);
  const safeLengthFactor = normalizePositiveFactor(lengthFactor, fallback);
  return safeForceFactor / safeLengthFactor;
};

export const calculateSurfaceLoadFactor = (forceFactor: unknown, lengthFactor: unknown, fallback = 1): number => {
  const safeForceFactor = normalizePositiveFactor(forceFactor, fallback);
  const safeLengthFactor = normalizePositiveFactor(lengthFactor, fallback);
  return safeForceFactor / Math.pow(safeLengthFactor, 2);
};

export const calculateMomentFactor = (forceFactor: unknown, lengthFactor: unknown, fallback = 1): number => {
  const safeForceFactor = normalizePositiveFactor(forceFactor, fallback);
  const safeLengthFactor = normalizePositiveFactor(lengthFactor, fallback);
  return safeForceFactor * safeLengthFactor;
};

export const createLineLoadUnit = (forceUnit: unknown, lengthUnit: unknown): string => {
  const safeForceUnit = safeUnit(forceUnit, "kN");
  const safeLengthUnit = safeUnit(lengthUnit, "m");
  return `${safeForceUnit}/${safeLengthUnit}`;
};

/** Load per unit area - snow on the ground, and anything else quoted per square metre. */
export const createSurfaceLoadUnit = (forceUnit: unknown, lengthUnit: unknown): string => {
  const safeForceUnit = safeUnit(forceUnit, "kN");
  const safeLengthUnit = safeUnit(lengthUnit, "m");
  return `${safeForceUnit}/${safeLengthUnit}²`;
};

export const createMomentUnit = (forceUnit: unknown, lengthUnit: unknown): string => {
  const safeForceUnit = safeUnit(forceUnit, "kN");
  const safeLengthUnit = safeUnit(lengthUnit, "m");
  return `${safeForceUnit}*${safeLengthUnit}`;
};

const toSuperscript = (value: number): string => {
  const superscriptDigits = ["⁰", "¹", "²", "³", "⁴", "⁵", "⁶", "⁷", "⁸", "⁹"];
  const integerText = Math.max(0, Math.floor(value)).toString();
  return integerText
    .split("")
    .map((char) => superscriptDigits[Number(char)] ?? "")
    .join("");
};

export const createSectionalPoweredUnit = (sectionalUnit: unknown, exponent: number): string => {
  const safeSectionalUnit = safeUnit(sectionalUnit, "mm");
  return `${safeSectionalUnit}${toSuperscript(exponent)}`;
};

export const normalizePositiveFactor = (factor: unknown, fallback = 1): number => {
  return typeof factor === "number" && Number.isFinite(factor) && factor > 0 ? factor : fallback;
};

export const factorWithExponent = (factor: unknown, exponent = 1, fallback = 1): number => {
  const safe = normalizePositiveFactor(factor, fallback);
  return Math.pow(safe, exponent);
};

export const createScaleConverter = (factor: unknown, exponent = 1, fallback = 1) => {
  const scaledFactor = factorWithExponent(factor, exponent, fallback);

  return {
    factor: scaledFactor,
    toDisplay: (siValue: number) => siValue * scaledFactor,
    fromDisplay: (displayValue: number) => displayValue / scaledFactor,
  };
};

export const unitSlice = createAppSlice({
  name: "unit",
  initialState: state,
  reducers: {
    // setMaterials(state, action) {
    //     state.materials = action.payload;
    // }
  },
  selectors: {
    getLengthFactor: (sliceState: any) => safeFactor(sliceState.length?.factor),
    getForceFactor: (sliceState: any) => safeFactor(sliceState.force?.factor),
    getLineLoadFactor: (sliceState: any) =>
      calculateLineLoadFactor(sliceState.force?.factor, sliceState.length?.factor),
    getSurfaceLoadFactor: (sliceState: any) =>
      calculateSurfaceLoadFactor(sliceState.force?.factor, sliceState.length?.factor),
    getMomentFactor: (sliceState: any) => calculateMomentFactor(sliceState.force?.factor, sliceState.length?.factor),
    getAngleFactor: (sliceState: any) => safeFactor(sliceState.angle?.factor),
    getMassFactor: (sliceState: any) => safeFactor(sliceState.mass?.factor),
    getDensityFactor: (sliceState: any) => calculateDensityFactor(sliceState.mass?.factor, sliceState.length?.factor),
    getSectionalFactor: (sliceState: any) => safeFactor(sliceState.sectional?.factor),
    getStressFactor: (sliceState: any) => safeFactor(sliceState.stress?.factor),
    getStrainFactor: (sliceState: any) => safeFactor(sliceState.strain?.factor),

    getLengthUnit: (sliceState: any) => safeUnit(sliceState.length?.unit, "m"),
    getForceUnit: (sliceState: any) => safeUnit(sliceState.force?.unit, "kN"),
    getLineLoadUnit: (sliceState: any) => createLineLoadUnit(sliceState.force?.unit, sliceState.length?.unit),
    getSurfaceLoadUnit: (sliceState: any) => createSurfaceLoadUnit(sliceState.force?.unit, sliceState.length?.unit),
    getMomentUnit: (sliceState: any) => createMomentUnit(sliceState.force?.unit, sliceState.length?.unit),
    getAngleUnit: (sliceState: any) => safeUnit(sliceState.angle?.unit, "deg"),
    getMassUnit: (sliceState: any) => safeUnit(sliceState.mass?.unit, "kg"),
    getDensityUnit: (sliceState: any) => createDensityUnit(sliceState.mass?.unit, sliceState.length?.unit),
    getTemperatureUnit: (sliceState: any) => safeUnit(sliceState.temperature?.unit, "C"),
    getSectionalUnit: (sliceState: any) => safeUnit(sliceState.sectional?.unit, "mm"),
    getSectionalAreaUnit: (sliceState: any) => createSectionalPoweredUnit(sliceState.sectional?.unit, 2),
    getSectionalBendResistanceUnit: (sliceState: any) => createSectionalPoweredUnit(sliceState.sectional?.unit, 3),
    getSectionalStaticMomentUnit: (sliceState: any) => createSectionalPoweredUnit(sliceState.sectional?.unit, 3),
    getSectionalMomentOfInertiaUnit: (sliceState: any) => createSectionalPoweredUnit(sliceState.sectional?.unit, 4),
    getStressUnit: (sliceState: any) => safeUnit(sliceState.stress?.unit, "MPa"),
    getStrainUnit: (sliceState: any) => safeUnit(sliceState.strain?.unit, "‰"),
  },
});

export const {
  getLengthFactor,
  getForceFactor,
  getLineLoadFactor,
  getSurfaceLoadFactor,
  getMomentFactor,
  getAngleFactor,
  getMassFactor,
  getDensityFactor,
  getSectionalFactor,
  getStressFactor,
  getStrainFactor,
  getLengthUnit,
  getForceUnit,
  getLineLoadUnit,
  getSurfaceLoadUnit,
  getMomentUnit,
  getAngleUnit,
  getMassUnit,
  getDensityUnit,
  getTemperatureUnit,
  getSectionalUnit,
  getSectionalAreaUnit,
  getSectionalBendResistanceUnit,
  getSectionalStaticMomentUnit,
  getSectionalMomentOfInertiaUnit,
  getStressUnit,
  getStrainUnit,
} = unitSlice.selectors;

export function useUnit() {
  const lengthFactor = useAppSelector(getLengthFactor);
  const forceFactor = useAppSelector(getForceFactor);
  const lineLoadFactor = useAppSelector(getLineLoadFactor);
  const surfaceLoadFactor = useAppSelector(getSurfaceLoadFactor);
  const momentFactor = useAppSelector(getMomentFactor);
  const angleFactor = useAppSelector(getAngleFactor);
  const densityFactor = useAppSelector(getDensityFactor);
  const sectionalFactor = useAppSelector(getSectionalFactor);
  const stressFactor = useAppSelector(getStressFactor);
  const strainFactor = useAppSelector(getStrainFactor);

  const lengthUnit = useAppSelector(getLengthUnit);
  const forceUnit = useAppSelector(getForceUnit);
  const momentUnit = useAppSelector(getMomentUnit);
  const sectionalUnit = useAppSelector(getSectionalUnit);
  const sectionalAreaUnit = useAppSelector(getSectionalAreaUnit);
  const sectionalBendResistanceUnit = useAppSelector(getSectionalBendResistanceUnit);
  const sectionalStaticMomentUnit = useAppSelector(getSectionalStaticMomentUnit);
  const sectionalMomentOfInertiaUnit = useAppSelector(getSectionalMomentOfInertiaUnit);
  const lineLoadUnit = useAppSelector(getLineLoadUnit);
  const surfaceLoadUnit = useAppSelector(getSurfaceLoadUnit);
  const angleUnit = useAppSelector(getAngleUnit);
  const densityUnit = useAppSelector(getDensityUnit);
  const temperatureUnit = useAppSelector(getTemperatureUnit);
  const stressUnit = useAppSelector(getStressUnit);
  const strainUnit = useAppSelector(getStrainUnit);

  const lengthConverter = createScaleConverter(lengthFactor);
  const forceConverter = createScaleConverter(forceFactor);
  const lineLoadConverter = createScaleConverter(lineLoadFactor);
  const surfaceLoadConverter = createScaleConverter(surfaceLoadFactor);
  const momentConverter = createScaleConverter(momentFactor);
  const angleConverter = createScaleConverter(angleFactor);
  const densityConverter = createScaleConverter(densityFactor);
  const stressConverter = createScaleConverter(stressFactor);
  const strainConverter = createScaleConverter(strainFactor);
  const sectionalLengthConverter = createScaleConverter(sectionalFactor, 1);
  const sectionalAreaConverter = createScaleConverter(sectionalFactor, 2);
  const sectionalBendResistanceConverter = createScaleConverter(sectionalFactor, 3);
  const sectionalStaticMomentConverter = createScaleConverter(sectionalFactor, 3);
  const sectionalMomentOfInertiaConverter = createScaleConverter(sectionalFactor, 4);

  return {
    lengthUnit,
    forceUnit,
    momentUnit,
    sectionalUnit,
    sectionalAreaUnit,
    sectionalBendResistanceUnit,
    sectionalStaticMomentUnit,
    sectionalMomentOfInertiaUnit,
    lineLoadUnit,
    surfaceLoadUnit,
    angleUnit,
    densityUnit,
    temperatureUnit,
    stressUnit,
    strainUnit,
    toDisplaySectionalLength: sectionalLengthConverter.toDisplay,
    fromDisplaySectionalLength: sectionalLengthConverter.fromDisplay,
    toDisplaySectionalArea: sectionalAreaConverter.toDisplay,
    fromDisplaySectionalArea: sectionalAreaConverter.fromDisplay,
    toDisplaySectionalBendResistance: sectionalBendResistanceConverter.toDisplay,
    fromDisplaySectionalBendResistance: sectionalBendResistanceConverter.fromDisplay,
    toDisplaySectionalStaticMoment: sectionalStaticMomentConverter.toDisplay,
    fromDisplaySectionalStaticMoment: sectionalStaticMomentConverter.fromDisplay,
    toDisplaySectionalMomentOfInertia: sectionalMomentOfInertiaConverter.toDisplay,
    fromDisplaySectionalMomentOfInertia: sectionalMomentOfInertiaConverter.fromDisplay,
    toDisplayLength: lengthConverter.toDisplay,
    fromDisplayLength: lengthConverter.fromDisplay,
    toDisplayForce: forceConverter.toDisplay,
    fromDisplayForce: forceConverter.fromDisplay,
    toDisplayLineLoad: lineLoadConverter.toDisplay,
    fromDisplayLineLoad: lineLoadConverter.fromDisplay,
    toDisplaySurfaceLoad: surfaceLoadConverter.toDisplay,
    fromDisplaySurfaceLoad: surfaceLoadConverter.fromDisplay,
    toDisplayMoment: momentConverter.toDisplay,
    fromDisplayMoment: momentConverter.fromDisplay,
    toDisplayAngle: angleConverter.toDisplay,
    fromDisplayAngle: angleConverter.fromDisplay,
    toDisplayDensity: densityConverter.toDisplay,
    fromDisplayDensity: densityConverter.fromDisplay,
    toDisplayStress: stressConverter.toDisplay,
    fromDisplayStress: stressConverter.fromDisplay,
    toDisplayStrain: strainConverter.toDisplay,
    fromDisplayStrain: strainConverter.fromDisplay,
  };
}
